<template>
  <div
    ref="tray"
    class="sticky inset-x-0 bottom-0 z-20 mt-auto flex shrink-0 flex-col overflow-hidden"
  >
    <Transition
      enter-active-class="duration-200 ease-out"
      enter-from-class="translate-y-10 opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="translate-y-10 opacity-0"
    >
      <div
        v-if="open"
        class="mx-4 mb-5 mt-4 flex items-center justify-between gap-4 gap-y-2 overflow-hidden rounded-lg border border-gray-300 bg-gray-200 p-2 text-sm font-bold text-gray-600 drop-shadow-lg transition @container/tray dark:border-border dark:bg-card dark:text-white"
        :class="[wrap && `flex-wrap`]"
      >
        <slot :is-compact="isCompact"></slot>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onWatcherCleanup, ref, useTemplateRef, watch } from "vue";

import { provideTrayContext } from "./internal/tray-context";

const COMPACT_BREAKPOINT = 640;

const tray = useTemplateRef("tray");

const props = defineProps<{
  wrap?: boolean;
  open: boolean;
}>();

const isCompact = ref(false);

provideTrayContext({
  isCompact,
});

watch([tray, () => props.open], ([trayEl, open], [, prevOpen]) => {
  if (!trayEl) {
    return;
  }

  if (!open) {
    // If this is an initial mount where the tray has not been opened,
    // capture it once.
    if (!prevOpen) {
      const rect = trayEl.getBoundingClientRect();
      isCompact.value = rect.width < COMPACT_BREAKPOINT;
    }

    return;
  }

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    const width = entry.contentRect.width;

    isCompact.value = width < COMPACT_BREAKPOINT;
  });

  onWatcherCleanup(() => observer.disconnect());

  observer.observe(trayEl);
});
</script>
